import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Alert,
  Snackbar,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { clientPlanService } from '../services/clientPlanService';
import { clientService } from '../services/clientService';
import { useAuth } from '../contexts/AuthContext';
import { ClientPlan, CreateClientPlanDto, UpdateClientPlanDto } from '../types/planning';

const PlanningPage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { isSuperAdmin, user, isClient } = useAuth();

  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<ClientPlan | null>(null);
  const [filterClientId, setFilterClientId] = useState<string>('');
  const [filterStatus, setFilterStatus] = useState<string>('');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  const [formData, setFormData] = useState<CreateClientPlanDto>({
    planName: '',
    clientId: user?.clientId || '',
    description: '',
  });

  const [editFormData, setEditFormData] = useState<UpdateClientPlanDto>({
    planName: '',
    description: '',
    status: 'ACTIVE',
  });

  // Fetch plans
  const { data: plansData, isLoading: plansLoading } = useQuery({
    queryKey: ['client-plans', filterClientId, filterStatus],
    queryFn: () => clientPlanService.getPlans({
      clientId: filterClientId || undefined,
      status: filterStatus as any || undefined,
      // Planning module only — workflow saved orders (ORDER_DRAFT) never show here.
      planType: 'PLANNING',
    }),
  });

  // Fetch clients for dropdown (not needed for CLIENT users)
  const { data: clientsData } = useQuery({
    queryKey: ['clients-for-plans'],
    queryFn: () => clientService.getClients({ limit: 1000 }),
    enabled: !isClient, // CLIENT users don't need clients list
  });

  const clients = clientsData?.data || [];
  const plans = plansData?.data || [];

  // Create plan mutation
  const createPlanMutation = useMutation({
    mutationFn: (data: CreateClientPlanDto) => clientPlanService.createPlan(data),
    onSuccess: (newPlan) => {
      queryClient.invalidateQueries({ queryKey: ['client-plans'] });
      setOpenCreateDialog(false);
      setFormData({ planName: '', clientId: '', description: '' });
      
      // Redirect to Guest Blog Sites in selection mode
      navigate(`/sites?mode=select&planId=${newPlan.id}&clientId=${newPlan.clientId}&from=planning`);
    },
    onError: (error: any) => {
      setSnackbar({
        open: true,
        message: error.response?.data?.error || 'Failed to create plan',
        severity: 'error',
      });
    },
  });

  // Update plan mutation
  const updatePlanMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateClientPlanDto }) =>
      clientPlanService.updatePlan(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['client-plans'] });
      setOpenEditDialog(false);
      setSelectedPlan(null);
      setSnackbar({
        open: true,
        message: 'Plan updated successfully',
        severity: 'success',
      });
    },
    onError: (error: any) => {
      setSnackbar({
        open: true,
        message: error.response?.data?.error || 'Failed to update plan',
        severity: 'error',
      });
    },
  });

  // Delete plan mutation
  const deletePlanMutation = useMutation({
    mutationFn: (id: string) => clientPlanService.deletePlan(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['client-plans'] });
      setSnackbar({
        open: true,
        message: 'Plan deleted successfully',
        severity: 'success',
      });
    },
    onError: (error: any) => {
      setSnackbar({
        open: true,
        message: error.response?.data?.error || 'Failed to delete plan',
        severity: 'error',
      });
    },
  });

  const handleCreatePlan = () => {
    const clientId = isClient ? user?.clientId : formData.clientId;
    if (!formData.planName.trim() || !clientId) {
      setSnackbar({
        open: true,
        message: 'Please fill in all required fields',
        severity: 'error',
      });
      return;
    }
    createPlanMutation.mutate({ ...formData, clientId: clientId!, planType: 'PLANNING' });
  };

  const handleEditPlan = () => {
    if (!selectedPlan) return;
    if (!editFormData.planName?.trim()) {
      setSnackbar({
        open: true,
        message: 'Plan name is required',
        severity: 'error',
      });
      return;
    }
    updatePlanMutation.mutate({ id: selectedPlan.id, data: editFormData });
  };

  const handleDeletePlan = (plan: ClientPlan) => {
    if (window.confirm(`Are you sure you want to delete the plan "${plan.planName}"? This will remove all associated sites.`)) {
      deletePlanMutation.mutate(plan.id);
    }
  };

  const handleOpenEditDialog = (plan: ClientPlan) => {
    setSelectedPlan(plan);
    setEditFormData({
      planName: plan.planName,
      description: plan.description || '',
      status: plan.status,
    });
    setOpenEditDialog(true);
  };

  const handleSelectSites = (plan: ClientPlan) => {
    navigate(`/sites?mode=select&planId=${plan.id}&clientId=${plan.clientId}&from=planning`);
  };

  const handleViewSites = (plan: ClientPlan) => {
    navigate(`/sites?planId=${plan.id}&clientId=${plan.clientId}&from=planning`);
  };

  
  return (
    <Box sx={{ padding: '28px', display: 'flex', flexDirection: 'column', gap: '24px' }}>
      {/* Page Header */}
      <Box sx={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between' }}>
        <Box>
          <Typography sx={{ fontSize: '22px', fontWeight: 700, letterSpacing: '-0.5px' }}>
            Planning
          </Typography>
          <Typography sx={{ fontSize: '13px', color: 'var(--text-muted)', mt: 0.5 }}>
            Manage your monthly backlink campaigns and site selection plans
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setOpenCreateDialog(true)}
          sx={{
            background: 'var(--accent)',
            color: 'white',
            boxShadow: '0 1px 2px rgba(37,99,235,0.2)',
            '&:hover': {
              background: 'var(--accent-hover)',
              boxShadow: '0 4px 16px rgba(0,0,0,0.08), 0 1px 4px rgba(0,0,0,0.04)'
            }
          }}
        >
          Create Plan
        </Button>
      </Box>

      {/* Stats Row */}
      <Box sx={{ display: 'grid', gridTemplateColumns: isClient ? 'repeat(3, 1fr)' : 'repeat(4, 1fr)', gap: '14px' }}>
        {/* Total Plans Stat */}
        <Box sx={{
          background: 'var(--surface)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius)',
          padding: '16px 18px',
          boxShadow: 'var(--shadow)',
          display: 'flex',
          alignItems: 'center',
          gap: '14px',
          transition: 'all 0.2s',
          '&:hover': {
            boxShadow: '0 4px 16px rgba(0,0,0,0.08), 0 1px 4px rgba(0,0,0,0.04)',
            transform: 'translateY(-1px)'
          }
        }}>
          <Box sx={{
            width: '36px',
            height: '36px',
            borderRadius: '9px',
            background: 'var(--accent-soft)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '16px', height: '16px', color: 'var(--accent)' }}>
              <rect x="3" y="4" width="18" height="18" rx="2"/>
              <line x1="16" y1="2" x2="16" y2="6"/>
              <line x1="8" y1="2" x2="8" y2="6"/>
              <line x1="3" y1="10" x2="21" y2="10"/>
            </svg>
          </Box>
          <Box>
            <Box sx={{ fontSize: '20px', fontWeight: 700, letterSpacing: '-0.5px', lineHeight: 1 }}>
              {plans.length}
            </Box>
            <Box sx={{ fontSize: '11.5px', color: 'var(--text-muted)', mt: 0.5 }}>
              Total Plans
            </Box>
          </Box>
        </Box>

        {/* Active Plans Stat */}
        <Box sx={{
          background: 'var(--surface)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius)',
          padding: '16px 18px',
          boxShadow: 'var(--shadow)',
          display: 'flex',
          alignItems: 'center',
          gap: '14px',
          transition: 'all 0.2s',
          '&:hover': {
            boxShadow: '0 4px 16px rgba(0,0,0,0.08), 0 1px 4px rgba(0,0,0,0.04)',
            transform: 'translateY(-1px)'
          }
        }}>
          <Box sx={{
            width: '36px',
            height: '36px',
            borderRadius: '9px',
            background: 'var(--green-soft)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '16px', height: '16px', color: 'var(--green)' }}>
              <polyline points="20 6 9 17 4 12"/>
            </svg>
          </Box>
          <Box>
            <Box sx={{ fontSize: '20px', fontWeight: 700, letterSpacing: '-0.5px', lineHeight: 1 }}>
              {plans.filter(p => p.status === 'ACTIVE').length}
            </Box>
            <Box sx={{ fontSize: '11.5px', color: 'var(--text-muted)', mt: 0.5 }}>
              Active Plans
            </Box>
          </Box>
        </Box>

        {/* Sites Selected Stat */}
        <Box sx={{
          background: 'var(--surface)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius)',
          padding: '16px 18px',
          boxShadow: 'var(--shadow)',
          display: 'flex',
          alignItems: 'center',
          gap: '14px',
          transition: 'all 0.2s',
          '&:hover': {
            boxShadow: '0 4px 16px rgba(0,0,0,0.08), 0 1px 4px rgba(0,0,0,0.04)',
            transform: 'translateY(-1px)'
          }
        }}>
          <Box sx={{
            width: '36px',
            height: '36px',
            borderRadius: '9px',
            background: 'var(--amber-soft)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '16px', height: '16px', color: 'var(--amber)' }}>
              <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
              <circle cx="12" cy="10" r="3"/>
            </svg>
          </Box>
          <Box>
            <Box sx={{ fontSize: '20px', fontWeight: 700, letterSpacing: '-0.5px', lineHeight: 1 }}>
              {plans.reduce((sum, p) => sum + (p.siteCount || 0), 0)}
            </Box>
            <Box sx={{ fontSize: '11.5px', color: 'var(--text-muted)', mt: 0.5 }}>
              Sites Selected
            </Box>
          </Box>
        </Box>

        {!isClient && (
          <Box sx={{
            background: 'var(--surface)',
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius)',
            padding: '16px 18px',
            boxShadow: 'var(--shadow)',
            display: 'flex',
            alignItems: 'center',
            gap: '14px',
            transition: 'all 0.2s',
            '&:hover': {
              boxShadow: '0 4px 16px rgba(0,0,0,0.08), 0 1px 4px rgba(0,0,0,0.04)',
              transform: 'translateY(-1px)'
            }
          }}>
            <Box sx={{
              width: '36px',
              height: '36px',
              borderRadius: '9px',
              background: 'var(--purple-soft)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '16px', height: '16px', color: 'var(--purple)' }}>
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                <circle cx="9" cy="7" r="4"/>
              </svg>
            </Box>
            <Box>
              <Box sx={{ fontSize: '20px', fontWeight: 700, letterSpacing: '-0.5px', lineHeight: 1 }}>
                {new Set(plans.map(p => p.projectId).filter(Boolean)).size}
              </Box>
              <Box sx={{ fontSize: '11.5px', color: 'var(--text-muted)', mt: 0.5 }}>
                Projects Covered
              </Box>
            </Box>
          </Box>
        )}
      </Box>

      {/* Filter Bar */}
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        background: 'var(--surface)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius)',
        padding: '14px 18px',
        boxShadow: 'var(--shadow)'
      }}>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '14px', height: '14px', color: 'var(--text-muted)', flexShrink: 0 }}>
          <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/>
        </svg>
        <Typography sx={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.6px', whiteSpace: 'nowrap' }}>
          Filter
        </Typography>
        <Box sx={{ width: '1px', height: '20px', background: 'var(--border)' }} />
        {!isClient && (
          <FormControl sx={{ minWidth: '170px' }}>
            <Select
              value={filterClientId}
              onChange={(e) => setFilterClientId(e.target.value)}
            displayEmpty
            sx={{
              background: 'var(--bg)',
              border: '1px solid var(--border)',
              borderRadius: 'var(--radius-sm)',
              padding: '8px 12px',
              fontSize: '13px',
              color: 'var(--text-secondary)',
              '& .MuiSelect-select': {
                padding: '0 32px 0 12px',
                background: 'none',
                border: 'none'
              },
              '& .MuiOutlinedInput-notchedOutline': {
                border: 'none'
              },
              '&:hover': {
                background: 'var(--surface)'
              },
              '&.Mui-focused': {
                background: 'white',
                borderColor: 'var(--accent)',
                boxShadow: '0 0 0 3px rgba(37,99,235,0.08)'
              }
            }}
          >
            <MenuItem value="">All Clients</MenuItem>
            {clients.map((client) => (
              <MenuItem key={client.id} value={client.id}>
                {client.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        )}
        <FormControl sx={{ minWidth: '170px' }}>
          <Select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            displayEmpty
            sx={{
              background: 'var(--bg)',
              border: '1px solid var(--border)',
              borderRadius: 'var(--radius-sm)',
              padding: '8px 12px',
              fontSize: '13px',
              color: 'var(--text-secondary)',
              '& .MuiSelect-select': {
                padding: '0 32px 0 12px',
                background: 'none',
                border: 'none'
              },
              '& .MuiOutlinedInput-notchedOutline': {
                border: 'none'
              },
              '&:hover': {
                background: 'var(--surface)'
              },
              '&.Mui-focused': {
                background: 'white',
                borderColor: 'var(--accent)',
                boxShadow: '0 0 0 3px rgba(37,99,235,0.08)'
              }
            }}
          >
            <MenuItem value="">All Statuses</MenuItem>
            <MenuItem value="ACTIVE">Active</MenuItem>
            <MenuItem value="INACTIVE">Inactive</MenuItem>
            <MenuItem value="COMPLETED">Completed</MenuItem>
          </Select>
        </FormControl>
        <Box sx={{ flex: 1 }} />
        {/* View Toggle */}
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '2px',
          background: 'var(--surface2)',
          borderRadius: '7px',
          padding: '3px'
        }}>
          <IconButton
            size="small"
            onClick={() => setViewMode('grid')}
            sx={{
              width: '30px',
              height: '28px',
              borderRadius: '5px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: viewMode === 'grid' ? 'var(--surface)' : 'none',
              boxShadow: viewMode === 'grid' ? 'var(--shadow)' : 'none',
              '&:hover': {
                background: viewMode === 'grid' ? 'var(--surface)' : 'var(--bg)'
              }
            }}
            title="Grid view"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ 
              width: '14px', 
              height: '14px', 
              color: viewMode === 'grid' ? 'var(--text-primary)' : 'var(--text-muted)' 
            }}>
              <rect x="3" y="3" width="7" height="7"/>
              <rect x="14" y="3" width="7" height="7"/>
              <rect x="3" y="14" width="7" height="7"/>
              <rect x="14" y="14" width="7" height="7"/>
            </svg>
          </IconButton>
          <IconButton
            size="small"
            onClick={() => setViewMode('list')}
            sx={{
              width: '30px',
              height: '28px',
              borderRadius: '5px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: viewMode === 'list' ? 'var(--surface)' : 'none',
              boxShadow: viewMode === 'list' ? 'var(--shadow)' : 'none',
              '&:hover': {
                background: viewMode === 'list' ? 'var(--surface)' : 'var(--bg)'
              }
            }}
            title="List view"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ 
              width: '14px', 
              height: '14px', 
              color: viewMode === 'list' ? 'var(--text-primary)' : 'var(--text-muted)' 
            }}>
              <line x1="8" y1="6" x2="21" y2="6"/>
              <line x1="8" y1="12" x2="21" y2="12"/>
              <line x1="8" y1="18" x2="21" y2="18"/>
              <line x1="3" y1="6" x2="3.01" y2="6"/>
              <line x1="3" y1="12" x2="3.01" y2="12"/>
              <line x1="3" y1="18" x2="3.01" y2="18"/>
            </svg>
          </IconButton>
        </Box>
      </Box>

      {/* Plans Grid */}
      {plansLoading ? (
        <Box display="flex" justifyContent="center" p={4}>
          <Typography>Loading plans...</Typography>
        </Box>
      ) : plans.length === 0 ? (
        <Box sx={{
          textAlign: 'center',
          padding: '64px 24px',
          background: 'var(--surface)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius)',
          boxShadow: 'var(--shadow)'
        }}>
          <Box sx={{
            width: '52px',
            height: '52px',
            background: 'var(--accent-soft)',
            borderRadius: '14px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 16px'
          }}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '24px', height: '24px', color: 'var(--accent)' }}>
              <rect x="3" y="4" width="18" height="18" rx="2"/>
              <line x1="16" y1="2" x2="16" y2="6"/>
              <line x1="8" y1="2" x2="8" y2="6"/>
              <line x1="3" y1="10" x2="21" y2="10"/>
            </svg>
          </Box>
          <Typography sx={{ fontSize: '16px', fontWeight: 600, color: 'var(--text-primary)', mb: 1 }}>
            No plans found
          </Typography>
          <Typography sx={{ fontSize: '13px', color: 'var(--text-muted)', mb: 3 }}>
            Create your first plan to get started
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setOpenCreateDialog(true)}
            sx={{
              background: 'var(--accent)',
              color: 'white',
              boxShadow: '0 1px 2px rgba(37,99,235,0.2)',
              '&:hover': {
                background: 'var(--accent-hover)',
                boxShadow: '0 4px 16px rgba(0,0,0,0.08), 0 1px 4px rgba(0,0,0,0.04)'
              }
            }}
          >
            Create Plan
          </Button>
        </Box>
      ) : (
        viewMode === 'grid' ? (
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px' }}>
            {plans.map((plan) => (
              <Box
                key={plan.id}
                sx={{
                  background: 'var(--surface)',
                  border: '1px solid var(--border)',
                  borderRadius: 'var(--radius)',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04)',
                  display: 'flex',
                  flexDirection: 'column',
                  transition: 'all 0.2s ease',
                  overflow: 'hidden',
                  animation: 'fadeUp 0.35s ease forwards',
                  opacity: 0,
                  '&:hover': {
                    boxShadow: '0 4px 16px rgba(0,0,0,0.08), 0 1px 4px rgba(0,0,0,0.04)',
                    transform: 'translateY(-2px)',
                    borderColor: '#d0d7e8'
                  }
                }}
                style={{
                  animationDelay: `${plans.indexOf(plan) * 0.05}s`
                }}
              >
                {/* Status Bar */}
                <Box sx={{
                  height: '4px',
                  width: '100%',
                  background: plan.status === 'ACTIVE' 
                    ? 'linear-gradient(90deg, #22c55e, #16a34a)' 
                    : 'linear-gradient(90deg, #e2e8f0, #cbd5e1)'
                }} />
                
                {/* Card Body */}
                <Box sx={{ padding: '18px 20px', flex: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '10px', mb: '14px' }}>
                    <Typography sx={{ 
                      fontSize: '15px', 
                      fontWeight: 600, 
                      color: 'var(--text-primary)', 
                      letterSpacing: '-0.2px', 
                      lineHeight: 1.3,
                      flex: 1
                    }}>
                      {plan.planName}
                    </Typography>
                    <Box sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '5px',
                      padding: '3px 9px',
                      borderRadius: '99px',
                      fontSize: '11px',
                      fontWeight: 600,
                      whiteSpace: 'nowrap',
                      flexShrink: 0,
                      background: plan.status === 'ACTIVE' ? 'var(--green-soft)' : 'var(--surface2)',
                      color: plan.status === 'ACTIVE' ? 'var(--green)' : 'var(--text-muted)',
                      border: plan.status === 'ACTIVE' ? '1px solid var(--green-border)' : '1px solid var(--border)',
                      '&::before': {
                        content: '""',
                        width: '5px',
                        height: '5px',
                        borderRadius: '50%',
                        background: plan.status === 'ACTIVE' ? 'var(--green)' : 'var(--text-muted)',
                        marginRight: '2px'
                      }
                    }}>
                      {plan.status}
                    </Box>
                  </Box>
                  
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {!isClient && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '14px', height: '14px', color: 'var(--text-muted)', flexShrink: 0 }}>
                          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                          <circle cx="9" cy="7" r="4"/>
                        </svg>
                        <Typography sx={{ fontSize: '12.5px', color: 'var(--text-secondary)' }}>
                          Client: <Box component="span" sx={{ color: 'var(--text-primary)', fontWeight: 500 }}>{plan.client.name}</Box>
                        </Typography>
                      </Box>
                    )}

                    {plan.project?.projectName && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '14px', height: '14px', color: 'var(--text-muted)', flexShrink: 0 }}>
                          <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/>
                        </svg>
                        <Typography sx={{ fontSize: '12.5px', color: 'var(--text-secondary)' }}>
                          Project: <Box component="span" sx={{ color: 'var(--text-primary)', fontWeight: 500 }}>{plan.project.projectName}</Box>
                        </Typography>
                      </Box>
                    )}
                    
                    {plan.description && (
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '14px', height: '14px', color: 'var(--text-muted)', flexShrink: 0, marginTop: '2px' }}>
                          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                          <polyline points="14 2 14 8 20 8"/>
                          <line x1="16" y1="13" x2="8" y2="13"/>
                          <line x1="16" y1="17" x2="8" y2="17"/>
                          <polyline points="10 9 9 9 8 9"/>
                        </svg>
                        <Typography sx={{ fontSize: '12.5px', color: 'var(--text-secondary)', lineHeight: 1.4 }}>
                          {plan.description}
                        </Typography>
                      </Box>
                    )}
                    
                    <Box sx={{ mt: '4px' }}>
                      <Box sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px',
                        padding: '4px 10px',
                        background: 'var(--accent-soft)',
                        border: '1px solid #c7d7fd',
                        borderRadius: '99px',
                        fontSize: '12px',
                        fontWeight: 600,
                        color: 'var(--accent)'
                      }}>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '12px', height: '12px' }}>
                          <line x1="8" y1="6" x2="21" y2="6"/>
                          <line x1="8" y1="12" x2="21" y2="12"/>
                          <line x1="8" y1="18" x2="21" y2="18"/>
                          <line x1="3" y1="6" x2="3.01" y2="6"/>
                          <line x1="3" y1="12" x2="3.01" y2="12"/>
                          <line x1="3" y1="18" x2="3.01" y2="18"/>
                        </svg>
                        {plan.siteCount || 0} sites
                      </Box>
                    </Box>
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', mt: '2px' }}>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '14px', height: '14px', color: 'var(--text-muted)', flexShrink: 0 }}>
                        <rect x="3" y="4" width="18" height="18" rx="2"/>
                        <line x1="16" y1="2" x2="16" y2="6"/>
                        <line x1="8" y1="2" x2="8" y2="6"/>
                        <line x1="3" y1="10" x2="21" y2="10"/>
                      </svg>
                      <Typography sx={{ 
                        fontSize: '12px', 
                        color: 'var(--text-secondary)',
                        fontFamily: '"DM Mono", monospace'
                      }}>
                        Created {new Date(plan.createdAt).toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: 'numeric' })}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
                
                {/* Divider */}
                <Box sx={{ height: '1px', background: 'var(--border-light)', mx: '20px' }} />
                
                {/* Actions */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '12px 16px' }}>
                  <Button
                    size="small"
                    onClick={() => handleViewSites(plan)}
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '5px',
                      padding: '6px 10px',
                      borderRadius: '6px',
                      fontSize: '12px',
                      fontWeight: 500,
                      color: 'var(--text-secondary)',
                      background: 'none',
                      border: 'none',
                      '&:hover': {
                        background: 'var(--accent-soft)',
                        color: 'var(--accent)'
                      }
                    }}
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '13px', height: '13px' }}>
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                      <circle cx="12" cy="12" r="3"/>
                    </svg>
                    View Sites
                  </Button>
                  <Button
                    size="small"
                    onClick={() => handleSelectSites(plan)}
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '5px',
                      padding: '6px 10px',
                      borderRadius: '6px',
                      fontSize: '12px',
                      fontWeight: 500,
                      color: 'var(--text-secondary)',
                      background: 'none',
                      border: 'none',
                      '&:hover': {
                        background: 'var(--green-soft)',
                        color: 'var(--green)'
                      }
                    }}
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '13px', height: '13px' }}>
                      <line x1="12" y1="5" x2="12" y2="19"/>
                      <line x1="5" y1="12" x2="19" y2="12"/>
                    </svg>
                    Select Sites
                  </Button>
                  <Box sx={{ width: '1px', height: '16px', background: 'var(--border-light)', mx: '2px' }} />
                  <IconButton
                    size="small"
                    onClick={() => handleOpenEditDialog(plan)}
                    sx={{
                      padding: '6px',
                      color: 'var(--text-secondary)',
                      '&:hover': {
                        background: 'var(--amber-soft)',
                        color: 'var(--amber)'
                      }
                    }}
                  >
                    <EditIcon sx={{ fontSize: '13px' }} />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => handleDeletePlan(plan)}
                    sx={{
                      padding: '6px',
                      color: 'var(--text-secondary)',
                      '&:hover': {
                        background: 'var(--red-soft)',
                        color: 'var(--red)'
                      }
                    }}
                  >
                    <DeleteIcon sx={{ fontSize: '13px' }} />
                  </IconButton>
                </Box>
              </Box>
            ))}
            
            {/* Add New Plan Card */}
            <Box
              onClick={() => setOpenCreateDialog(true)}
              sx={{
                border: '2px dashed var(--border)',
                boxShadow: 'none',
                background: 'var(--bg)',
                cursor: 'pointer',
                opacity: 1,
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '220px',
                display: 'flex',
                '&:hover': {
                  borderColor: 'var(--accent)',
                  background: 'var(--accent-soft)'
                }
              }}
            >
              <Box sx={{ textAlign: 'center', padding: '32px 20px' }}>
                <Box sx={{
                  width: '44px',
                  height: '44px',
                  background: 'var(--accent-soft)',
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 14px'
                }}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '20px', height: '20px', color: 'var(--accent)' }}>
                    <line x1="12" y1="5" x2="12" y2="19"/>
                    <line x1="5" y1="12" x2="19" y2="12"/>
                  </svg>
                </Box>
                <Typography sx={{ fontSize: '14px', fontWeight: 600, color: 'var(--text-primary)', mb: 1 }}>
                  Create New Plan
                </Typography>
                <Typography sx={{ fontSize: '12.5px', color: 'var(--text-muted)' }}>
                  Start a new backlink campaign
                </Typography>
              </Box>
            </Box>
          </Box>
        ) : (
          /* List View */
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {plans.map((plan) => (
              <Box
                key={plan.id}
                sx={{
                  background: 'var(--surface)',
                  border: '1px solid var(--border)',
                  borderRadius: 'var(--radius)',
                  boxShadow: 'var(--shadow)',
                  display: 'flex',
                  alignItems: 'center',
                  padding: '16px 20px',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    boxShadow: '0 4px 16px rgba(0,0,0,0.08), 0 1px 4px rgba(0,0,0,0.04)',
                    borderColor: '#d0d7e8'
                  }
                }}
              >
                {/* Status Indicator */}
                <Box sx={{
                  width: '4px',
                  height: '40px',
                  borderRadius: '2px',
                  background: plan.status === 'ACTIVE' 
                    ? 'linear-gradient(180deg, #22c55e, #16a34a)' 
                    : 'linear-gradient(180deg, #e2e8f0, #cbd5e1)',
                  mr: '16px'
                }} />
                
                {/* Plan Info */}
                <Box sx={{ flex: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', mb: '8px' }}>
                    <Typography sx={{ 
                      fontSize: '15px', 
                      fontWeight: 600, 
                      color: 'var(--text-primary)', 
                      letterSpacing: '-0.2px'
                    }}>
                      {plan.planName}
                    </Typography>
                    <Box sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '5px',
                      padding: '3px 9px',
                      borderRadius: '99px',
                      fontSize: '11px',
                      fontWeight: 600,
                      whiteSpace: 'nowrap',
                      background: plan.status === 'ACTIVE' ? 'var(--green-soft)' : 'var(--surface2)',
                      color: plan.status === 'ACTIVE' ? 'var(--green)' : 'var(--text-muted)',
                      border: plan.status === 'ACTIVE' ? '1px solid var(--green-border)' : '1px solid var(--border)',
                      '&::before': {
                        content: '""',
                        width: '5px',
                        height: '5px',
                        borderRadius: '50%',
                        background: plan.status === 'ACTIVE' ? 'var(--green)' : 'var(--text-muted)',
                        marginRight: '2px'
                      }
                    }}>
                      {plan.status}
                    </Box>
                  </Box>
                  
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                    {!isClient && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '14px', height: '14px', color: 'var(--text-muted)' }}>
                          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                          <circle cx="9" cy="7" r="4"/>
                        </svg>
                        <Typography sx={{ fontSize: '12.5px', color: 'var(--text-secondary)' }}>
                          <Box component="span" sx={{ color: 'var(--text-primary)', fontWeight: 500 }}>{plan.client.name}</Box>
                        </Typography>
                      </Box>
                    )}

                    {plan.project?.projectName && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '14px', height: '14px', color: 'var(--text-muted)' }}>
                          <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/>
                        </svg>
                        <Typography sx={{ fontSize: '12.5px', color: 'var(--text-secondary)' }}>
                          <Box component="span" sx={{ color: 'var(--text-primary)', fontWeight: 500 }}>{plan.project.projectName}</Box>
                        </Typography>
                      </Box>
                    )}
                    
                    <Box sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '6px',
                      padding: '4px 10px',
                      background: 'var(--accent-soft)',
                      border: '1px solid #c7d7fd',
                      borderRadius: '99px',
                      fontSize: '12px',
                      fontWeight: 600,
                      color: 'var(--accent)'
                    }}>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '12px', height: '12px' }}>
                        <line x1="8" y1="6" x2="21" y2="6"/>
                        <line x1="8" y1="12" x2="21" y2="12"/>
                        <line x1="8" y1="18" x2="21" y2="18"/>
                        <line x1="3" y1="6" x2="3.01" y2="6"/>
                        <line x1="3" y1="12" x2="3.01" y2="12"/>
                        <line x1="3" y1="18" x2="3.01" y2="18"/>
                      </svg>
                      {plan.siteCount || 0} sites
                    </Box>
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '14px', height: '14px', color: 'var(--text-muted)' }}>
                        <rect x="3" y="4" width="18" height="18" rx="2"/>
                        <line x1="16" y1="2" x2="16" y2="6"/>
                        <line x1="8" y1="2" x2="8" y2="6"/>
                        <line x1="3" y1="10" x2="21" y2="10"/>
                      </svg>
                      <Typography sx={{ 
                        fontSize: '12px', 
                        color: 'var(--text-secondary)',
                        fontFamily: '"DM Mono", monospace'
                      }}>
                        {new Date(plan.createdAt).toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: 'numeric' })}
                      </Typography>
                    </Box>
                    
                    {plan.description && (
                      <Typography sx={{ fontSize: '12.5px', color: 'var(--text-muted)', fontStyle: 'italic' }}>
                        "{plan.description.length > 50 ? plan.description.substring(0, 50) + '...' : plan.description}"
                      </Typography>
                    )}
                  </Box>
                </Box>
                
                {/* Actions */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', ml: '16px' }}>
                  <Button
                    size="small"
                    onClick={() => handleViewSites(plan)}
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '5px',
                      padding: '6px 10px',
                      borderRadius: '6px',
                      fontSize: '12px',
                      fontWeight: 500,
                      color: 'var(--text-secondary)',
                      background: 'none',
                      border: 'none',
                      '&:hover': {
                        background: 'var(--accent-soft)',
                        color: 'var(--accent)'
                      }
                    }}
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '13px', height: '13px' }}>
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                      <circle cx="12" cy="12" r="3"/>
                    </svg>
                    View Sites
                  </Button>
                  <Button
                    size="small"
                    onClick={() => handleSelectSites(plan)}
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '5px',
                      padding: '6px 10px',
                      borderRadius: '6px',
                      fontSize: '12px',
                      fontWeight: 500,
                      color: 'var(--text-secondary)',
                      background: 'none',
                      border: 'none',
                      '&:hover': {
                        background: 'var(--green-soft)',
                        color: 'var(--green)'
                      }
                    }}
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '13px', height: '13px' }}>
                      <line x1="12" y1="5" x2="12" y2="19"/>
                      <line x1="5" y1="12" x2="19" y2="12"/>
                    </svg>
                    Select Sites
                  </Button>
                  <Box sx={{ width: '1px', height: '16px', background: 'var(--border-light)', mx: '2px' }} />
                  <IconButton
                    size="small"
                    onClick={() => handleOpenEditDialog(plan)}
                    sx={{
                      padding: '6px',
                      color: 'var(--text-secondary)',
                      '&:hover': {
                        background: 'var(--amber-soft)',
                        color: 'var(--amber)'
                      }
                    }}
                  >
                    <EditIcon sx={{ fontSize: '13px' }} />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => handleDeletePlan(plan)}
                    sx={{
                      padding: '6px',
                      color: 'var(--text-secondary)',
                      '&:hover': {
                        background: 'var(--red-soft)',
                        color: 'var(--red)'
                      }
                    }}
                  >
                    <DeleteIcon sx={{ fontSize: '13px' }} />
                  </IconButton>
                </Box>
              </Box>
            ))}
            
            {/* Add New Plan Button for List View */}
            <Box
              onClick={() => setOpenCreateDialog(true)}
              sx={{
                border: '2px dashed var(--border)',
                boxShadow: 'none',
                background: 'var(--bg)',
                cursor: 'pointer',
                borderRadius: 'var(--radius)',
                padding: '24px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                '&:hover': {
                  borderColor: 'var(--accent)',
                  background: 'var(--accent-soft)'
                }
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <Box sx={{
                  width: '44px',
                  height: '44px',
                  background: 'var(--accent-soft)',
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '20px', height: '20px', color: 'var(--accent)' }}>
                    <line x1="12" y1="5" x2="12" y2="19"/>
                    <line x1="5" y1="12" x2="19" y2="12"/>
                  </svg>
                </Box>
                <Box>
                  <Typography sx={{ fontSize: '14px', fontWeight: 600, color: 'var(--text-primary)', mb: 1 }}>
                    Create New Plan
                  </Typography>
                  <Typography sx={{ fontSize: '12.5px', color: 'var(--text-muted)' }}>
                    Start a new backlink campaign
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>
        )
      )}

      {/* Create Plan Dialog */}
      <Dialog 
        open={openCreateDialog} 
        onClose={() => setOpenCreateDialog(false)} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
            boxShadow: '0 16px 48px rgba(0,0,0,0.12), 0 4px 16px rgba(0,0,0,0.06)',
            border: '1px solid var(--border)',
            overflow: 'hidden'
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          alignItems: 'flex-start', 
          justifyContent: 'space-between',
          padding: '22px 26px 18px',
          borderBottom: '1px solid var(--border-light)'
        }}>
          <Box>
            <Typography sx={{ fontSize: '16px', fontWeight: 600, letterSpacing: '-0.3px' }}>
              Create New Plan
            </Typography>
            <Typography sx={{ fontSize: '12.5px', color: 'var(--text-muted)', mt: 0.5 }}>
              Set up a new backlink planning campaign
            </Typography>
          </Box>
          <IconButton
            onClick={() => setOpenCreateDialog(false)}
            sx={{
              width: '30px',
              height: '30px',
              borderRadius: '6px',
              '&:hover': { background: 'var(--bg)' }
            }}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '16px', height: '16px', color: 'var(--text-muted)' }}>
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </IconButton>
        </DialogTitle>
        
        {/* Step Indicator */}
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: '8px', 
          padding: '12px 26px',
          background: 'var(--bg)',
          borderBottom: '1px solid var(--border-light)'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', fontWeight: 500, color: 'var(--accent)' }}>
            <Box sx={{
              width: '20px',
              height: '20px',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '10px',
              fontWeight: 700,
              background: 'var(--accent)',
              color: 'white'
            }}>
              1
            </Box>
            Plan Details
          </Box>
          <Box sx={{ flex: 1, height: '1px', background: 'var(--border)' }} />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', fontWeight: 500, color: 'var(--text-muted)' }}>
            <Box sx={{
              width: '20px',
              height: '20px',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '10px',
              fontWeight: 700,
              background: 'var(--border)',
              color: 'var(--text-muted)'
            }}>
              2
            </Box>
            Select Sites
          </Box>
          <Box sx={{ flex: 1, height: '1px', background: 'var(--border)' }} />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', fontWeight: 500, color: 'var(--text-muted)' }}>
            <Box sx={{
              width: '20px',
              height: '20px',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '10px',
              fontWeight: 700,
              background: 'var(--border)',
              color: 'var(--text-muted)'
            }}>
              3
            </Box>
            Review
          </Box>
        </Box>
        
        <DialogContent sx={{ padding: '22px 26px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <Typography sx={{ fontSize: '12px', fontWeight: 500, color: 'var(--text-secondary)' }}>
              Plan Name <Box component="span" sx={{ color: 'var(--red)', ml: '2px' }}>*</Box>
            </Typography>
            <TextField
              fullWidth
              value={formData.planName}
              onChange={(e) => setFormData({ ...formData, planName: e.target.value })}
              placeholder="e.g. March 2026 Order"
              required
              sx={{
                '& .MuiOutlinedInput-root': {
                  background: 'var(--surface)',
                  border: '1px solid var(--border)',
                  borderRadius: 'var(--radius-sm)',
                  '& fieldset': { border: 'none' },
                  '&:hover': { background: 'var(--bg)' },
                  '&.Mui-focused': {
                    background: 'white',
                    borderColor: 'var(--accent)',
                    boxShadow: '0 0 0 3px rgba(37,99,235,0.08)'
                  }
                }
              }}
            />
          </Box>
          
          {!isClient && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <Typography sx={{ fontSize: '12px', fontWeight: 500, color: 'var(--text-secondary)' }}>
                Client <Box component="span" sx={{ color: 'var(--red)', ml: '2px' }}>*</Box>
              </Typography>
              <FormControl fullWidth>
                <Select
                  value={formData.clientId}
                  onChange={(e) => setFormData({ ...formData, clientId: e.target.value })}
                displayEmpty
                sx={{
                  background: 'var(--surface)',
                  border: '1px solid var(--border)',
                  borderRadius: 'var(--radius-sm)',
                  '& .MuiSelect-select': {
                    padding: '10px 12px',
                    background: 'none',
                    border: 'none'
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    border: 'none'
                  },
                  '&:hover': {
                    background: 'var(--bg)'
                  },
                  '&.Mui-focused': {
                    background: 'white',
                    borderColor: 'var(--accent)',
                    boxShadow: '0 0 0 3px rgba(37,99,235,0.08)'
                  }
                }}
              >
                <MenuItem value="" disabled>Select a client…</MenuItem>
                {clients.map((client) => (
                  <MenuItem key={client.id} value={client.id}>
                    {client.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            </Box>
          )}
          
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <Typography sx={{ fontSize: '12px', fontWeight: 500, color: 'var(--text-secondary)' }}>
              Description <Box component="span" sx={{ color: 'var(--text-muted)', fontWeight: 400, ml: '2px' }}>(Optional)</Box>
            </Typography>
            <TextField
              fullWidth
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Add a brief description of this plan…"
              multiline
              rows={3}
              sx={{
                '& .MuiOutlinedInput-root': {
                  background: 'var(--surface)',
                  border: '1px solid var(--border)',
                  borderRadius: 'var(--radius-sm)',
                  '& fieldset': { border: 'none' },
                  '&:hover': { background: 'var(--bg)' },
                  '&.Mui-focused': {
                    background: 'white',
                    borderColor: 'var(--accent)',
                    boxShadow: '0 0 0 3px rgba(37,99,235,0.08)'
                  }
                }
              }}
            />
          </Box>
        </DialogContent>
        
        <DialogActions sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'flex-end', 
          gap: '10px', 
          padding: '16px 26px',
          borderTop: '1px solid var(--border-light)'
        }}>
          <Button 
            onClick={() => setOpenCreateDialog(false)}
            sx={{
              background: 'var(--surface)',
              color: 'var(--text-secondary)',
              border: '1px solid var(--border)',
              '&:hover': {
                background: 'var(--bg)',
                color: 'var(--text-primary)'
              }
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleCreatePlan}
            disabled={createPlanMutation.isPending}
            sx={{
              background: 'var(--accent)',
              color: 'white',
              boxShadow: '0 1px 2px rgba(37,99,235,0.2)',
              '&:hover': {
                background: 'var(--accent-hover)',
                boxShadow: '0 4px 16px rgba(0,0,0,0.08), 0 1px 4px rgba(0,0,0,0.04)'
              }
            }}
          >
            {createPlanMutation.isPending ? 'Creating...' : 'Next: Select Sites'}
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '14px', height: '14px', marginLeft: '6px' }}>
              <polyline points="9 18 15 12 9 6"/>
            </svg>
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Plan Dialog */}
      <Dialog 
        open={openEditDialog} 
        onClose={() => setOpenEditDialog(false)} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
            boxShadow: '0 16px 48px rgba(0,0,0,0.12), 0 4px 16px rgba(0,0,0,0.06)',
            border: '1px solid var(--border)',
            overflow: 'hidden'
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          alignItems: 'flex-start', 
          justifyContent: 'space-between',
          padding: '22px 26px 18px',
          borderBottom: '1px solid var(--border-light)'
        }}>
          <Box>
            <Typography sx={{ fontSize: '16px', fontWeight: 600, letterSpacing: '-0.3px' }}>
              Edit Plan
            </Typography>
            <Typography sx={{ fontSize: '12.5px', color: 'var(--text-muted)', mt: 0.5 }}>
              Update this plan's details and status
            </Typography>
          </Box>
          <IconButton
            onClick={() => setOpenEditDialog(false)}
            sx={{
              width: '30px',
              height: '30px',
              borderRadius: '6px',
              '&:hover': { background: 'var(--bg)' }
            }}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '16px', height: '16px', color: 'var(--text-muted)' }}>
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </IconButton>
        </DialogTitle>
        
        <DialogContent sx={{ padding: '22px 26px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <Typography sx={{ fontSize: '12px', fontWeight: 500, color: 'var(--text-secondary)' }}>
              Plan Name <Box component="span" sx={{ color: 'var(--red)', ml: '2px' }}>*</Box>
            </Typography>
            <TextField
              fullWidth
              value={editFormData.planName}
              onChange={(e) => setEditFormData({ ...editFormData, planName: e.target.value })}
              required
              sx={{
                '& .MuiOutlinedInput-root': {
                  background: 'var(--surface)',
                  border: '1px solid var(--border)',
                  borderRadius: 'var(--radius-sm)',
                  '& fieldset': { border: 'none' },
                  '&:hover': { background: 'var(--bg)' },
                  '&.Mui-focused': {
                    background: 'white',
                    borderColor: 'var(--accent)',
                    boxShadow: '0 0 0 3px rgba(37,99,235,0.08)'
                  }
                }
              }}
            />
          </Box>
          
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <Typography sx={{ fontSize: '12px', fontWeight: 500, color: 'var(--text-secondary)' }}>
              Description <Box component="span" sx={{ color: 'var(--text-muted)', fontWeight: 400, ml: '2px' }}>(Optional)</Box>
            </Typography>
            <TextField
              fullWidth
              value={editFormData.description}
              onChange={(e) => setEditFormData({ ...editFormData, description: e.target.value })}
              placeholder="Add a brief description…"
              multiline
              rows={3}
              sx={{
                '& .MuiOutlinedInput-root': {
                  background: 'var(--surface)',
                  border: '1px solid var(--border)',
                  borderRadius: 'var(--radius-sm)',
                  '& fieldset': { border: 'none' },
                  '&:hover': { background: 'var(--bg)' },
                  '&.Mui-focused': {
                    background: 'white',
                    borderColor: 'var(--accent)',
                    boxShadow: '0 0 0 3px rgba(37,99,235,0.08)'
                  }
                }
              }}
            />
          </Box>
          
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <Typography sx={{ fontSize: '12px', fontWeight: 500, color: 'var(--text-secondary)' }}>
              Status
            </Typography>
            <FormControl fullWidth>
              <Select
                value={editFormData.status}
                onChange={(e) => setEditFormData({ ...editFormData, status: e.target.value as any })}
                sx={{
                  background: 'var(--surface)',
                  border: '1px solid var(--border)',
                  borderRadius: 'var(--radius-sm)',
                  '& .MuiSelect-select': {
                    padding: '10px 12px',
                    background: 'none',
                    border: 'none'
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    border: 'none'
                  },
                  '&:hover': {
                    background: 'var(--bg)'
                  },
                  '&.Mui-focused': {
                    background: 'white',
                    borderColor: 'var(--accent)',
                    boxShadow: '0 0 0 3px rgba(37,99,235,0.08)'
                  }
                }}
              >
                <MenuItem value="ACTIVE">Active</MenuItem>
                <MenuItem value="INACTIVE">Inactive</MenuItem>
                <MenuItem value="COMPLETED">Completed</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        
        <DialogActions sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'flex-end', 
          gap: '10px', 
          padding: '16px 26px',
          borderTop: '1px solid var(--border-light)'
        }}>
          <Button 
            onClick={() => setOpenEditDialog(false)}
            sx={{
              background: 'var(--surface)',
              color: 'var(--text-secondary)',
              border: '1px solid var(--border)',
              '&:hover': {
                background: 'var(--bg)',
                color: 'var(--text-primary)'
              }
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleEditPlan}
            disabled={updatePlanMutation.isPending}
            sx={{
              background: 'var(--accent)',
              color: 'white',
              boxShadow: '0 1px 2px rgba(37,99,235,0.2)',
              '&:hover': {
                background: 'var(--accent-hover)',
                boxShadow: '0 4px 16px rgba(0,0,0,0.08), 0 1px 4px rgba(0,0,0,0.04)'
              }
            }}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '14px', height: '14px', marginRight: '6px' }}>
              <polyline points="20 6 9 17 4 12"/>
            </svg>
            Update Plan
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      </Box>
  );
};

export default PlanningPage;
